import { useState, useRef, useEffect } from "react";
import { Sparkle, PaperPlaneRight } from "@phosphor-icons/react";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "Draft a WhatsApp intro for a first-time buyer looking for 3BHK in Sector 82",
  "How do I handle a customer negotiating on token amount?",
  "Give me 5 questions to qualify a hot lead",
  "Compare Sector 82 vs Sector 66-A for luxury buyers in Mohali",
];

export default function AIChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(`ai-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const q = text || input;
    if (!q.trim() || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ session_id: sessionId.current, message: q }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.delta) {
              setMessages(m => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + payload.delta };
                return copy;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-9rem)]" data-testid="ai-chat-page">
      <div className="mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#D4AF37]"><Sparkle weight="duotone" /> AI Assistant</div>
        <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">Nawab AI • Powered by Claude Sonnet 4.5</h1>
      </div>

      <div className="flex-1 card-luxury p-6 overflow-y-auto space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#F2CE6E] to-[#A17E1A] flex items-center justify-center gold-glow">
              <Sparkle size={30} weight="duotone" className="text-[#060B14]" />
            </div>
            <h3 className="font-serif text-xl text-white mt-4">How can I help you close more deals today?</h3>
            <p className="text-slate-400 text-sm mt-2">Ask about scripts, objections, market insights, or lead qualification.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => send(s)} data-testid={`ai-suggest-${i}`}
                  className="text-left p-3 rounded-md border border-[#1E293B] hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5 transition-colors text-sm text-slate-300">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-4 py-3 rounded-xl ${m.role === "user" ? "bg-[#D4AF37] text-[#060B14]" : "bg-[#060B14] border border-[#1E293B] text-slate-200"}`}>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}{m.role === "assistant" && streaming && i === messages.length - 1 && <span className="inline-block w-1 h-4 bg-[#D4AF37] animate-pulse ml-1" />}</div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={e => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask anything about your leads, properties, or the Mohali market…"
          data-testid="ai-input"
          className="flex-1 bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-4 py-3 text-white text-sm" />
        <button type="submit" disabled={streaming || !input.trim()} data-testid="ai-send-btn"
          className="px-5 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold hover:bg-[#F2CE6E] disabled:opacity-50 flex items-center gap-2">
          <PaperPlaneRight weight="fill" size={16} />
        </button>
      </form>
    </div>
  );
}
